import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, Gift, CreditCard, Sparkles, ArrowRight } from 'lucide-react';
import appleImg from '../images/Apple Gift Card.png';
import googleImg from '../images/Google Play Credit.png';
import ytImg from '../images/YouTube Premium.png';

const PremiumCards = () => {
  const cards = [
    { 
      id: 'apple-card', 
      title: 'Apple Gift Card', 
      brand: 'bg-gradient-to-br from-gray-900 to-black',
      icon: appleImg,
      description: 'Up to $100 in credits',
      iconBg: 'bg-gray-900',
      color: 'text-gray-200',
      accent: 'from-gray-600 to-gray-800',
      iconElement: <Gift className="w-6 h-6 md:w-8 md:h-8 text-gray-300" />,
      gradient: 'from-gray-900 via-black to-gray-900'
    },
    { 
      id: 'google-play', 
      title: 'Google Play Credit', 
      brand: 'bg-gradient-to-br from-blue-900 to-blue-700',
      icon: googleImg,
      description: 'Premium app credits',
      iconBg: 'bg-blue-900',
      color: 'text-blue-100',
      accent: 'from-blue-600 to-blue-800',
      iconElement: <CreditCard className="w-6 h-6 md:w-8 md:h-8 text-blue-300" />,
      gradient: 'from-blue-900 via-blue-800 to-blue-900'
    },
    { 
      id: 'youtube', 
      title: 'YouTube Premium', 
      brand: 'bg-gradient-to-br from-red-900 to-red-700',
      icon: ytImg,
      description: 'Ad-free experience',
      iconBg: 'bg-red-900',
      color: 'text-red-100',
      accent: 'from-red-600 to-red-800',
      iconElement: <Play className="w-6 h-6 md:w-8 md:h-8 text-red-300" />,
      gradient: 'from-red-900 via-red-800 to-red-900'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95 
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
        mass: 0.8
      }
    },
    hover: {
      y: -12,
      scale: 1.02,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25
      }
    }
  };

  const imageVariants = {
    hover: {
      scale: 1.15,
      transition: {
        duration: 0.6,
        ease: [0.43, 0.13, 0.23, 0.96]
      }
    }
  };

  const contentVariants = {
    hover: {
      y: -5,
      transition: {
        type: 'spring',
        stiffness: 300
      }
    }
  };

  const iconVariants = {
    initial: { rotate: 0, scale: 1 },
    hover: { 
      rotate: [0, 10, -10, 0],
      scale: 1.1,
      transition: {
        rotate: {
          duration: 0.5,
          times: [0, 0.25, 0.75, 1]
        },
        scale: {
          duration: 0.3
        }
      }
    }
  };

  const buttonVariants = {
    initial: { width: '0%', opacity: 0 },
    hover: { 
      width: '100%', 
      opacity: 1,
      transition: {
        width: {
          duration: 0.3,
          ease: 'easeInOut'
        },
        opacity: {
          duration: 0.2
        }
      }
    }
  };

  const shimmerVariants = {
    initial: { x: '-100%' },
    hover: {
      x: '100%',
      transition: {
        duration: 0.8,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <motion.div 
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {cards.map((item, index) => (
        <motion.div
          key={item.id}
          variants={cardVariants}
          whileHover="hover"
          className="relative group cursor-pointer"
        >
          {/* Glow effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-100 blur-xl rounded-3xl"
            style={{
              background: `linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899)`
            }}
            animate={{
              opacity: [0, 0.3, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />

          <Link to={`/offer/${item.id}`}>
            <div className={`relative rounded-3xl overflow-hidden h-48 md:h-full ${item.brand} border border-gray-800/50 shadow-2xl`}>
              
              {/* Background image with overlay */}
              <div className="absolute inset-0">
                <motion.img
                  src={item.icon}
                  alt={item.title}
                  className="w-full h-full object-cover opacity-40"
                  variants={imageVariants}
                />
                
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-b ${item.gradient} opacity-90`} />
                
                {/* Shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                  variants={shimmerVariants}
                />
              </div>

              {/* Floating particles */}
              <div className="absolute inset-0">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-white rounded-full"
                    initial={{ 
                      x: `${20 + i * 30}%`,
                      y: `${30 + i * 20}%`,
                      opacity: 0
                    }}
                    animate={{
                      y: [`${30 + i * 20}%`, `${10 + i * 20}%`, `${30 + i * 20}%`],
                      opacity: [0, 0.5, 0],
                      scale: [0, 1, 0]
                    }}
                    transition={{
                      duration: 2 + i * 0.5,
                      repeat: Infinity,
                      delay: i * 0.3,
                      ease: 'easeInOut'
                    }}
                  />
                ))}
              </div>

              {/* Content */}
              <div className="relative h-full p-6 md:p-8 flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  {/* Icon */}
                  <motion.div
                    variants={iconVariants}
                    className={`p-3 md:p-4 rounded-2xl ${item.iconBg} backdrop-blur-sm border border-white/10`}
                  >
                    {item.iconElement}
                  </motion.div>

                  {/* Sparkle badge */}
                  <motion.div
                    initial={{ rotate: 0 }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="opacity-60"
                  >
                    <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </motion.div>
                </div>

                <motion.div variants={contentVariants}>
                  <div className="space-y-2 md:space-y-3">
                    <h3 className={`font-bold text-xl md:text-2xl ${item.color} leading-tight`}>
                      {item.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-300/80 font-medium">
                      {item.description}
                    </p>
                  </div>

                  {/* CTA Button */}
                  <motion.div className="mt-4 md:mt-6 overflow-hidden">
                    <motion.div
                      variants={buttonVariants}
                      className="h-[1px] bg-gradient-to-r from-transparent via-white to-transparent mb-3"
                    />
                    <div className="flex items-center gap-2 p-1">
                      <span className={`text-sm md:text-[15px] font-semibold ${item.color}`}>
                        Claim Offer
                      </span>
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-white" />
                      </motion.div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>

              {/* External link indicator */}
              <motion.div
                className="absolute top-4 right-4"
                initial={{ opacity: 0, scale: 0 }}
                whileHover={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring' }}
              >
                <ArrowRight className="w-4 h-4 text-white/60" />
              </motion.div>

              {/* Hover border animation */}
              <motion.div
                className="absolute inset-0 rounded-3xl border-2 border-white/10"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
              />
            </div>

            {/* Subtle shadow elevation */}
            <motion.div
              className="absolute inset-0 bg-black/20 rounded-3xl -z-10"
              initial={{ y: 0, opacity: 0.5 }}
              whileHover={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
          </Link>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default PremiumCards;